import React, { useState, useEffect } from 'react';
import {
    IconButton,
    Tooltip,
    useColorModeValue,
    keyframes
} from '@chakra-ui/react';
import { MdKeyboardArrowUp } from 'react-icons/md';

/**
 * Simple Back to Top Button Component
 */
function BackToTop() {
    const [isVisible, setIsVisible] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);

    // Theme-aware colors
    const bgColor = useColorModeValue('brand.500', 'brand.400');
    const hoverBgColor = useColorModeValue('brand.600', 'brand.500');
    const shadowColor = useColorModeValue('rgba(67, 24, 255, 0.3)', 'rgba(67, 24, 255, 0.4)');

    // Animation for smooth appearance
    const fadeIn = keyframes`
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
    `;

    /**
     * Check scroll position across different scroll contexts
     * Returns the maximum scroll position found
     */
    const getScrollPosition = () => {
        const windowScroll = window.pageYOffset || 0;
        const documentScroll = document.documentElement.scrollTop || 0;
        const bodyScroll = document.body.scrollTop || 0;
        
        // Check main content area
        const mainElement = document.querySelector('main');
        const mainScroll = mainElement ? mainElement.scrollTop : 0;
        
        // Return the maximum scroll position found
        return Math.max(windowScroll, documentScroll, bodyScroll, mainScroll);
    };

    /**
     * Monitor scroll position for button visibility
     */
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = getScrollPosition();
            setIsVisible(scrollPosition > 300);
        };

        // Add scroll listeners to multiple elements to catch all scroll events
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('scroll', handleScroll, { passive: true });
        
        // Also listen on the main content area
        const mainElement = document.querySelector('main');
        if (mainElement) {
            mainElement.addEventListener('scroll', handleScroll, { passive: true });
        }

        // Initial check
        handleScroll();

        // Cleanup
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('scroll', handleScroll);
            if (mainElement) {
                mainElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    /**
     * Scroll to top using multiple fallback methods
     * Tries different scroll targets to ensure it works
     */
    const scrollToTop = () => {
        setIsScrolling(true);

        try {
            // Method 1: Try smooth scroll on window
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });

            // Method 2: Try main content area
            const mainElement = document.querySelector('main');
            if (mainElement && mainElement.scrollTop > 0) {
                mainElement.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }

            // Method 3: Try document element
            const docElement = document.documentElement;
            if (docElement && docElement.scrollTop > 0) {
                docElement.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }

            // Method 4: Try body element
            if (document.body && document.body.scrollTop > 0) {
                document.body.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }

            // Monitor completion
            const checkCompletion = () => {
                const currentScroll = getScrollPosition();
                if (currentScroll <= 10) {
                    setIsScrolling(false);
                } else {
                    requestAnimationFrame(checkCompletion);
                }
            };

            // Start monitoring
            setTimeout(() => requestAnimationFrame(checkCompletion), 100);

            // Fallback timeout
            setTimeout(() => {
                setIsScrolling(false);
            }, 1500);

        } catch (error) {
            console.error('Scroll failed, trying fallback methods:', error);
            
            // Fallback: Instant scroll
            try {
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                
                const mainElement = document.querySelector('main');
                if (mainElement) {
                    mainElement.scrollTop = 0;
                }
            } catch (fallbackError) {
                console.error('All scroll methods failed:', fallbackError);
            }
            
            setIsScrolling(false);
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <Tooltip 
            label="Back to top" 
            hasArrow 
            placement="left"
            bg="gray.800"
            color="white"
            fontSize="sm"
        >
            <IconButton
                aria-label="Back to top"
                icon={<MdKeyboardArrowUp />}
                onClick={scrollToTop}
                isLoading={isScrolling}
                loadingText=""
                size="lg"
                position="fixed"
                bottom={{ base: "20px", md: "30px" }}
                right={{ base: "20px", md: "30px" }}
                zIndex={9999}
                bg={bgColor}
                color="white"
                borderRadius="full"
                boxShadow={`0 4px 20px ${shadowColor}`}
                _hover={{
                    bg: hoverBgColor,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 25px ${shadowColor}`
                }}
                _active={{
                    transform: 'translateY(0)',
                    boxShadow: `0 2px 10px ${shadowColor}`
                }}
                animation={`${fadeIn} 0.3s ease-out`}
                transition="all 0.2s ease"
                _focus={{
                    outline: '2px solid',
                    outlineColor: 'brand.300',
                    outlineOffset: '2px'
                }}
                // Ensure it works on mobile
                sx={{
                    '@media (max-width: 768px)': {
                        width: '48px',
                        height: '48px',
                        fontSize: '20px'
                    }
                }}
            />
        </Tooltip>
    );
}

export default BackToTop;